import { createRoute } from "@tanstack/react-router";
import { Route as TenantLayoutRoute } from "./_layout";
import { BadgeWithDot } from "@/components/base/badges/badges";
import { Card, CardBody, CardHeader } from "@/components/application/card/card";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useTenantSyncStatus } from "../../hooks/useTenantSyncStatus";

/**
 * Stav dat střediska (princip #4 — sync transparency): uživatel vždy ví, jestli
 * jsou data kompletní/částečná a z jakého času pochází.
 */

const STATE_LABELS: Record<string, { label: string; color: "success" | "warning" | "error" | "gray" }> = {
  kompletni: { label: "Data kompletní", color: "success" },
  castecny: { label: "Částečná synchronizace", color: "warning" },
  selhal: { label: "Synchronizace selhala", color: "error" },
  running: { label: "Synchronizace probíhá", color: "gray" },
};

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("cs-CZ", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function TenantSyncStatusPage() {
  const { data, isLoading, error } = useTenantSyncStatus();
  const meta = data?.state ? STATE_LABELS[data.state] ?? { label: data.state, color: "gray" as const } : null;

  return (
    <section aria-labelledby="sync-status-title" className="max-w-3xl mx-auto space-y-6">
      <header className="flex flex-col gap-1">
        <h2 id="sync-status-title" className="text-2xl font-bold text-primary m-0">Stav dat</h2>
        <p className="text-base text-tertiary m-0">
          Aktuálnost dat střediska ze systému Axigon.
        </p>
      </header>

      {error && (
        <InlineAlert variant="error">
          {error instanceof Error ? error.message : "Načtení stavu selhalo."}
        </InlineAlert>
      )}

      <Card>
        <CardHeader><h3 className="text-lg font-semibold text-primary m-0">Poslední synchronizace</h3></CardHeader>
        <CardBody>
          {isLoading ? (
            <LoadingIndicator label="Načítání…" />
          ) : !data?.state ? (
            <p className="text-tertiary m-0">Zatím neproběhla žádná synchronizace.</p>
          ) : (
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 m-0">
              <div>
                <dt className="text-sm text-tertiary">Stav</dt>
                <dd className="m-0 mt-1">
                  {meta && <BadgeWithDot color={meta.color}>{meta.label}</BadgeWithDot>}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-tertiary">Poslední sync</dt>
                <dd className="m-0 mt-1 text-primary">{formatTime(data.last_sync_at)}</dd>
              </div>
              <div>
                <dt className="text-sm text-tertiary">Počet položek</dt>
                <dd className="m-0 mt-1 text-primary">{data.inventory_count ?? "—"}</dd>
              </div>
            </dl>
          )}
        </CardBody>
      </Card>

      {data && !data.complete && data.state && (
        <InlineAlert variant="warning" title="Data nemusí být kompletní">
          Poslední synchronizace nebyla kompletní — zobrazené hodnoty se mohou ještě změnit.
        </InlineAlert>
      )}
    </section>
  );
}

export const Route = createRoute({
  getParentRoute: () => TenantLayoutRoute,
  path: "/tenant/sync-status",
  component: TenantSyncStatusPage,
});
